import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, History, FileText, Loader2, Presentation, Pencil, Check, X, ImagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CLASSIFICATION_OPTIONS = ["LTI", "MWD", "MI", "FAI"];

export default function OverviewTab({ analysis, onReportGenerated }) {
  const { user } = useAuth();
  const canEditClassification = user?.role === "Administrateur national" || user?.role === "Administrateur Site";
  const [history, setHistory] = useState([]);
  const [categories, setCategories] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [exportingPptx, setExportingPptx] = useState(false);
  const [editingClassification, setEditingClassification] = useState(false);
  const [classBase, setClassBase] = useState("");
  const [classCritique, setClassCritique] = useState(false);
  const [savingClassification, setSavingClassification] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsForm, setDetailsForm] = useState({});
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  const generateReport = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/hfacs/${analysis.ID_HFACS}/report`);
      toast.success("Rapport PDF généré et enregistré dans le dossier Drive.");
      window.open(res.data.url, "_blank", "noopener,noreferrer");
      onReportGenerated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la génération du rapport.");
    } finally {
      setGenerating(false);
    }
  };

  const exportPptx = async () => {
    setExportingPptx(true);
    try {
      const res = await axios.post(`${API}/hfacs/${analysis.ID_HFACS}/export-pptx`);
      toast.success("Export PowerPoint généré et enregistré dans le dossier Drive.");
      window.open(res.data.url, "_blank", "noopener,noreferrer");
      onReportGenerated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'export PowerPoint.");
    } finally {
      setExportingPptx(false);
    }
  };

  const openClassificationEditor = () => {
    const current = analysis.CLASSIFICATION || "";
    setClassBase(current.replace(" Critique", "").trim());
    setClassCritique(current.includes("Critique"));
    setEditingClassification(true);
  };

  const saveClassification = async () => {
    setSavingClassification(true);
    try {
      const classification = classBase ? `${classBase}${classCritique ? " Critique" : ""}` : "";
      await axios.put(`${API}/hfacs/${analysis.ID_HFACS}/classification`, { classification });
      toast.success("Classification mise à jour.");
      setEditingClassification(false);
      onReportGenerated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la mise à jour de la classification.");
    } finally {
      setSavingClassification(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(`${API}/hfacs/${analysis.ID_HFACS}/photos`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo ajoutée au dossier Drive de l'analyse (diapo Photos du PowerPoint).");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi de la photo.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const openDetailsEditor = () => {
    setDetailsForm({
      date_evenement: analysis.DATE_EVENEMENT || "",
      heure_evenement: analysis.HEURE_EVENEMENT || "",
      type_evenement: analysis.TYPE_EVENEMENT || "",
      personne_concernee: analysis.PERSONNE_CONCERNEE || "",
      categorie_personne: analysis.CATEGORIE_PERSONNE || "",
      consequence: analysis.CONSEQUENCE || "",
      description_initiale: analysis.DESCRIPTION_INITIALE || "",
      localisation: analysis.LOCALISATION || "",
      activite: analysis.ACTIVITE || "",
      entreprise: analysis.ENTREPRISE || "",
      equipe_analyse: analysis.EQUIPE_ANALYSE || "",
      d3_actions_protection: analysis.D3_ACTIONS_PROTECTION || "",
      d3_prime_elements_analyse: analysis.D3_PRIME_ELEMENTS_ANALYSE || "",
      d3_seconde_actions_immediates: analysis.D3_SECONDE_ACTIONS_IMMEDIATES || "",
    });
    setEditingDetails(true);
  };

  const saveDetails = async () => {
    setSavingDetails(true);
    try {
      await axios.put(`${API}/hfacs/${analysis.ID_HFACS}/details`, detailsForm);
      toast.success("Informations de l'analyse mises à jour.");
      setEditingDetails(false);
      onReportGenerated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la mise à jour.");
    } finally {
      setSavingDetails(false);
    }
  };

  const setField = (key) => (e) => setDetailsForm((f) => ({ ...f, [key]: e.target.value }));

  useEffect(() => {
    if (!analysis?.ID_HFACS) return;
    axios.get(`${API}/hfacs/${analysis.ID_HFACS}/history`).then((res) => setHistory(res.data));
  }, [analysis?.ID_HFACS]);

  useEffect(() => {
    axios.get(`${API}/parametres`).then((res) => setCategories(res.data.filter((p) => p.CATEGORIE === "CATEGORIE_PERSONNE")));
  }, []);

  if (!analysis) return null;
  const fields = [
    ["Date de l'événement", `${analysis.DATE_EVENEMENT || "—"} ${analysis.HEURE_EVENEMENT || ""}`],
    ["Type d'événement", analysis.TYPE_EVENEMENT],
    ["Personne concernée", analysis.PERSONNE_CONCERNEE || "—"],
    ["Catégorie", analysis.CATEGORIE_PERSONNE],
    ["Localisation", analysis.LOCALISATION || "—"],
    ["Activité", analysis.ACTIVITE || "—"],
    ["Entreprise", analysis.ENTREPRISE || "—"],
    ["Analyste", analysis.ANALYSTE],
    ["Équipe d'analyse", analysis.EQUIPE_ANALYSE || "—"],
  ];

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-md p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading uppercase font-semibold text-sm text-muted-foreground">Description initiale</h3>
          {!editingDetails && (
            <button data-testid="details-edit-trigger" onClick={openDetailsEditor} className="text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1 text-xs">
              <Pencil className="h-3 w-3" /> Modifier
            </button>
          )}
        </div>

        {editingDetails ? (
          <div className="space-y-5">
            <div>
              <Textarea data-testid="details-edit-description" value={detailsForm.description_initiale} onChange={setField("description_initiale")} rows={3} />
            </div>
            <div>
              <p className="text-[11px] font-mono uppercase text-muted-foreground mb-1">Conséquence</p>
              <Textarea data-testid="details-edit-consequence" value={detailsForm.consequence} onChange={setField("consequence")} rows={2} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="text-[11px] font-mono uppercase text-muted-foreground mb-1">Date de l'événement</p>
                <Input type="date" data-testid="details-edit-date" value={detailsForm.date_evenement} onChange={setField("date_evenement")} />
              </div>
              <div>
                <p className="text-[11px] font-mono uppercase text-muted-foreground mb-1">Heure de l'événement</p>
                <Input type="time" data-testid="details-edit-heure" value={detailsForm.heure_evenement} onChange={setField("heure_evenement")} />
              </div>
              <div>
                <p className="text-[11px] font-mono uppercase text-muted-foreground mb-1">Type d'événement</p>
                <Input data-testid="details-edit-type" value={detailsForm.type_evenement} onChange={setField("type_evenement")} />
              </div>
              <div>
                <p className="text-[11px] font-mono uppercase text-muted-foreground mb-1">Catégorie</p>
                <Select value={detailsForm.categorie_personne} onValueChange={(v) => setDetailsForm((f) => ({ ...f, categorie_personne: v }))}>
                  <SelectTrigger data-testid="details-edit-categorie"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c.VALEUR} value={c.VALEUR}>{c.VALEUR}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-[11px] font-mono uppercase text-muted-foreground mb-1">Personne concernée</p>
                <Input data-testid="details-edit-personne" value={detailsForm.personne_concernee} onChange={setField("personne_concernee")} />
              </div>
              <div>
                <p className="text-[11px] font-mono uppercase text-muted-foreground mb-1">Localisation</p>
                <Input data-testid="details-edit-localisation" value={detailsForm.localisation} onChange={setField("localisation")} />
              </div>
              <div>
                <p className="text-[11px] font-mono uppercase text-muted-foreground mb-1">Activité</p>
                <Input data-testid="details-edit-activite" value={detailsForm.activite} onChange={setField("activite")} />
              </div>
              <div>
                <p className="text-[11px] font-mono uppercase text-muted-foreground mb-1">Entreprise</p>
                <Input data-testid="details-edit-entreprise" value={detailsForm.entreprise} onChange={setField("entreprise")} />
              </div>
              <div>
                <p className="text-[11px] font-mono uppercase text-muted-foreground mb-1">Équipe d'analyse (noms séparés par virgule)</p>
                <Input data-testid="details-edit-equipe" value={detailsForm.equipe_analyse} onChange={setField("equipe_analyse")} />
              </div>
            </div>
            <div className="space-y-4 pt-2 border-t border-border/60">
              <div>
                <p className="text-[11px] font-mono uppercase text-muted-foreground mb-1">D3 — Actions immédiates de protection</p>
                <Textarea data-testid="details-edit-d3-protection" value={detailsForm.d3_actions_protection} onChange={setField("d3_actions_protection")} rows={2} />
              </div>
              <div>
                <p className="text-[11px] font-mono uppercase text-muted-foreground mb-1">D3' — Premiers éléments d'analyse</p>
                <Textarea data-testid="details-edit-d3-prime" value={detailsForm.d3_prime_elements_analyse} onChange={setField("d3_prime_elements_analyse")} rows={2} />
              </div>
              <div>
                <p className="text-[11px] font-mono uppercase text-muted-foreground mb-1">D3'' — Actions immédiates prises</p>
                <Textarea data-testid="details-edit-d3-seconde" value={detailsForm.d3_seconde_actions_immediates} onChange={setField("d3_seconde_actions_immediates")} rows={2} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" disabled={savingDetails} data-testid="details-save-button" onClick={saveDetails} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
                {savingDetails ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enregistrer
              </Button>
              <Button size="sm" variant="outline" data-testid="details-cancel-button" onClick={() => setEditingDetails(false)}>
                <X className="h-4 w-4" /> Annuler
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p data-testid="analysis-description" className="text-sm leading-relaxed">{analysis.DESCRIPTION_INITIALE}</p>
            {analysis.CONSEQUENCE && (
              <>
                <h3 className="font-heading uppercase font-semibold text-sm text-muted-foreground mt-4 mb-2">Conséquence</h3>
                <p className="text-sm leading-relaxed">{analysis.CONSEQUENCE}</p>
              </>
            )}
          </>
        )}
      </div>

      {!editingDetails && (
      <div className="bg-card border border-border rounded-md p-5 grid sm:grid-cols-2 gap-4">
        {fields.map(([label, value]) => (
          <div key={label}>
            <p className="text-[11px] font-mono uppercase text-muted-foreground">{label}</p>
            <p className="text-sm mt-0.5">{value}</p>
          </div>
        ))}
        <div>
          <p className="text-[11px] font-mono uppercase text-muted-foreground">Classification</p>
          {editingClassification ? (
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Select value={classBase} onValueChange={setClassBase}>
                <SelectTrigger data-testid="classification-edit-select" className="h-8 w-28 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {CLASSIFICATION_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1.5">
                <Checkbox id="overview-critique" data-testid="classification-edit-critique" checked={classCritique} onCheckedChange={(v) => setClassCritique(!!v)} />
                <label htmlFor="overview-critique" className="text-xs cursor-pointer">Critique</label>
              </div>
              <Button size="icon" variant="ghost" className="h-7 w-7" disabled={savingClassification} data-testid="classification-save-button" onClick={saveClassification}>
                {savingClassification ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingClassification(false)}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-0.5">
              <p data-testid="classification-value" className="text-sm">{analysis.CLASSIFICATION || "—"}</p>
              {canEditClassification && (
                <button data-testid="classification-edit-trigger" onClick={openClassificationEditor} className="text-muted-foreground hover:text-foreground transition-colors">
                  <Pencil className="h-3 w-3" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {!editingDetails && (analysis.D3_ACTIONS_PROTECTION || analysis.D3_PRIME_ELEMENTS_ANALYSE || analysis.D3_SECONDE_ACTIONS_IMMEDIATES) && (
        <div className="bg-card border border-border rounded-md p-5 space-y-4" data-testid="d3-fields-display">
          {analysis.D3_ACTIONS_PROTECTION && (
            <div>
              <h3 className="font-heading uppercase font-semibold text-sm text-muted-foreground mb-1">D3 — Actions immédiates de protection</h3>
              <p className="text-sm leading-relaxed whitespace-pre-line">{analysis.D3_ACTIONS_PROTECTION}</p>
            </div>
          )}
          {analysis.D3_PRIME_ELEMENTS_ANALYSE && (
            <div>
              <h3 className="font-heading uppercase font-semibold text-sm text-muted-foreground mb-1">D3' — Premiers éléments d'analyse</h3>
              <p className="text-sm leading-relaxed whitespace-pre-line">{analysis.D3_PRIME_ELEMENTS_ANALYSE}</p>
            </div>
          )}
          {analysis.D3_SECONDE_ACTIONS_IMMEDIATES && (
            <div>
              <h3 className="font-heading uppercase font-semibold text-sm text-muted-foreground mb-1">D3'' — Actions immédiates prises</h3>
              <p className="text-sm leading-relaxed whitespace-pre-line">{analysis.D3_SECONDE_ACTIONS_IMMEDIATES}</p>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-4 flex-wrap">
        {analysis.URL_DOSSIER_DRIVE && (
          <a
            href={analysis.URL_DOSSIER_DRIVE}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="analysis-drive-link"
            className="inline-flex items-center gap-2 text-sm text-accent hover:underline"
          >
            <ExternalLink className="h-4 w-4" />
            Ouvrir le dossier Drive de l'analyse
          </a>
        )}
        <input ref={photoInputRef} type="file" accept="image/*" className="hidden" data-testid="photo-upload-input" onChange={uploadPhoto} />
        <Button
          onClick={() => photoInputRef.current?.click()}
          disabled={uploadingPhoto}
          data-testid="upload-photo-button"
          size="sm"
          variant="outline"
          className="gap-2"
        >
          {uploadingPhoto ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          Ajouter des photos
        </Button>
        <Button onClick={generateReport} disabled={generating} data-testid="generate-report-button" size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
          {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
          Générer le rapport PDF
        </Button>
        {analysis.URL_RAPPORT_PDF && (
          <a
            href={analysis.URL_RAPPORT_PDF}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="last-report-link"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground hover:underline"
          >
            <FileText className="h-4 w-4" />
            Dernier rapport généré
          </a>
        )}
        <Button onClick={exportPptx} disabled={exportingPptx} data-testid="export-pptx-button" size="sm" variant="outline" className="gap-2">
          {exportingPptx ? <Loader2 className="h-4 w-4 animate-spin" /> : <Presentation className="h-4 w-4" />}
          Exporter en PowerPoint
        </Button>
        {analysis.URL_RAPPORT_PPTX && (
          <a
            href={analysis.URL_RAPPORT_PPTX}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="last-pptx-link"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground hover:underline"
          >
            <Presentation className="h-4 w-4" />
            Dernier export PowerPoint
          </a>
        )}
      </div>

      <div className="bg-card border border-border rounded-md p-5">
        <div className="flex items-center gap-2 mb-3 text-muted-foreground">
          <History className="h-4 w-4" />
          <h3 className="font-heading uppercase font-semibold text-sm">Historique</h3>
        </div>
        <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="analysis-history-list">
          {history.map((h) => (
            <div key={h.ID_LOG} className="text-xs border-b border-border/60 pb-2 last:border-b-0">
              <span className="font-mono text-muted-foreground">{h.DATE_HEURE?.slice(0, 19).replace("T", " ")}</span>
              {" · "}
              <span className="text-foreground">{h.UTILISATEUR}</span>
              {" — "}
              <span>{h.ACTION} {h.OBJET}</span>
              {h.NOUVELLE_VALEUR && <span className="text-muted-foreground"> ({h.NOUVELLE_VALEUR.slice(0, 80)})</span>}
            </div>
          ))}
          {history.length === 0 && <p className="text-xs text-muted-foreground">Aucun historique pour le moment.</p>}
        </div>
      </div>
    </div>
  );
}
